import {
    Material,
    Primitive,
    GeometryInstance,
    Cartesian3,
    Matrix4,
    MaterialAppearance,
    Transforms,
    VertexFormat,
    Color,
} from 'cesium';
import Grate from "./Grate.js";
import Well from "./Well.js";
import transformRgb from "../_util/transformRgb";

let _cache = {};

// 获取立方体
const getBoxGeometry = () => {
    if (_cache.boxGeometry) return _cache.boxGeometry;

    _cache.boxGeometry = Grate.fromDimensions({
        vertexFormat: VertexFormat.POSITION_NORMAL_AND_ST,
        dimensions: new Cartesian3(1.5, 1, 4.0) // 长宽高
    })

    return _cache.boxGeometry
}

// 获取圆柱
const getCylinderGeometry = () => {
    if (_cache.cylinderGeometry) return _cache.cylinderGeometry;

    const cylinder = new Well({
        length: 5.0, // 长度
        topRadius: 1.5, // 顶部半径
        bottomRadius: 1.5, // 底部半径
        // slices: 4
    })

    _cache.cylinderGeometry = Well.createGeometry(cylinder);

    return _cache.cylinderGeometry
}

// 根据类型获取几何体
const getGeometryByType = (type) => {
    const [TYPE] = (type || 'cylinder').split('_');
    return {
        box: getBoxGeometry,
        cylinder: getCylinderGeometry,
        grate: getBoxGeometry,
        well: getCylinderGeometry
    }[TYPE]()
}

class ThreePointLayer {
    constructor(options) {
        const {show = true} = options;
        this.map = options.map;

        // 图层标识
        this.key = options.key;

        // 图层名称
        this.name = options.name;

        // 标签字段
        this.labelField = options.labelField;

        // 模版内容
        this.template = options.template;

        // 图例
        this.legend = options.legend;

        // 图层控制
        this.layerControl = options.layerControl;

        // 取消气泡
        this.cancelPopup = options.cancelPopup;

        // 气泡模板
        this.tipTemplate = options.tipTemplate;

        // 仅展示
        this.viewOnly = options.viewOnly;

        this.style = options.style;

        this.data = options.data;

        this.color = options.color;

        this.primitives = [];

        this.show = show;

        if (this.data?.length) {
            this.loadData(this.data)
        }
    }

    set show(value) {
        if (this.primitives?.length) {
            this.primitives.map(primitive => {
                primitive.show = value;
            })
        }
    }

    loadData(data) {
        this.data = data;
        const map = this.map;
        const show = this.show;
        const style = this.style;

        this.destroy();

        this.primitives = [];

        const types = {}, colors = {};

        data.map((item) => {
            let {data} = item;
            const type = item.type || 'cylinder';
            const color = item.color || this.color;

            let geometry = getGeometryByType(type);

            // 缓存颜色对应的数据
            if (color) {
                // 缓存颜色
                colors[color] = colors[color] || {
                    item,
                    _instances: [],
                    data: [],
                    color: color
                };

                // 缓存颜色对应的数据
                colors[color]._instances.push(new GeometryInstance({
                    geometry,
                    modelMatrix: Matrix4.multiplyByTranslation(Transforms.eastNorthUpToFixedFrame(
                            Cartesian3.fromDegrees(data[0], data[1])),
                        new Cartesian3(0.0, 0.0, data[2] || 0.0),
                        new Matrix4()
                    ),
                    id: item.id,
                }))

                colors[color].data.push(item);
            } else if (style[type]) {
                // 缓存颜色
                types[type] = types[type] || {
                    item,
                    _instances: [],
                    data: [],
                    type,
                };

                types[type]._instances.push(new GeometryInstance({
                    geometry,
                    modelMatrix: Matrix4.multiplyByTranslation(Transforms.eastNorthUpToFixedFrame(
                            Cartesian3.fromDegrees(data[0], data[1])),
                        new Cartesian3(0.0, 0.0, data[2] || 0.0),
                        new Matrix4()
                    ),
                    id: item.id,
                }))

                types[type].data.push(item)
            }
        });

        for (let color_str in colors) {
            const {color, data, _instances} = colors[color_str];

            const primitive = new Primitive({
                geometryInstances: _instances,
                show,
                appearance: new MaterialAppearance({
                    material: new Material({
                        fabric: {
                            type: 'Color',
                            uniforms: {
                                color: Color.fromCssColorString(transformRgb(color, 1))
                            }
                        }
                    }),
                    faceForward: true
                }),
                asynchronous: false,
            })
            this.setAttribute(primitive, data);
            map.scene.primitives.add(primitive);
            this.primitives.push(primitive);
        }

        for (let type in types) {
            const {data, _instances} = types[type];

            const primitive = new Primitive({
                geometryInstances: _instances,
                show,
                appearance: new MaterialAppearance({
                    material: new Material({
                        fabric: {
                            type: 'Image',
                            uniforms: {
                                image: style[type]
                            }
                        }
                    }),
                    faceForward: true,
                }),
                asynchronous: false,
            })
            this.setAttribute(primitive, data);
            map.scene.primitives.add(primitive);
            this.primitives.push(primitive);
        }

        this.loaded?.(this.primitives)
    }

    setAttribute(primitive, data) {
        // 图层标识
        primitive.key = this.key;

        // 数据
        primitive.data = data;

        // 图层名称
        primitive.name = this.name;

        // 标签字段
        primitive.labelField = this.labelField;

        // 模版内容
        primitive.template = this.template;

        // 图例
        primitive.legend = this.legend;

        // 图层控制
        primitive.layerControl = this.layerControl;

        // 取消气泡
        primitive.cancelPopup = this.cancelPopup;

        // 气泡模板
        primitive.tipTemplate = this.tipTemplate;

        // 仅展示
        primitive.viewOnly = this.viewOnly;
    }

    destroy() {
        const map = this.map;

        if (this.primitives?.length) {
            this.primitives.map(primitive => {
                map.scene.primitives.remove(primitive);
            })
            this.primitives = null;
        }
    }
}

export default ThreePointLayer;
